import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import qs from 'qs';

import { FOCUS_RESOURCE_QUERY_PARAM } from 'floorplan/pages/native-floorplan/const';
import { useSearchParams } from 'hooks/use-search-params.hook';
import { replace } from 'store/router/actions';
import { searchParams as SearchParams } from 'shared/types/search-params';

interface UseFocusedResourceValues {
  /** The placement a coworker jump asked the map to centre on; null when none. */
  focusedResourceUuid: string | null;
  clearFocus: VoidFunction;
}

/**
 * The jump's target, read from the url (`focusResource`). Focus is not selection: it is the map
 * answering "where does this coworker sit" — centred, zoomed, ringed — and it lasts until the user
 * points at something else. Tapping any desk clears it (see `useNativeFloorplanMap`).
 */
export const useFocusedResource = (): UseFocusedResourceValues => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const queryParams = useSearchParams<SearchParams>();
  const focusedResourceUuid = queryParams[FOCUS_RESOURCE_QUERY_PARAM] || null;

  const clearFocus = useCallback(() => {
    if (!queryParams[FOCUS_RESOURCE_QUERY_PARAM]) {
      return;
    }

    // `qs.stringify` leaves undefined values out, which is how the param is removed.
    dispatch(replace(`${pathname}?${qs.stringify({ ...queryParams, [FOCUS_RESOURCE_QUERY_PARAM]: undefined })}`));
  }, [dispatch, pathname, queryParams]);

  return useMemo(() => ({ focusedResourceUuid, clearFocus }), [focusedResourceUuid, clearFocus]);
};
